import { useCallback, useContext, useEffect, useMemo, useState } from 'react'
import { AbcOutlined, AddOutlined, DataObjectOutlined, DeleteOutlined } from '@mui/icons-material'
import { Box, Button, Grid, Skeleton, Tooltip } from '@mui/material'
import {
  DataGridPro,
  GridActionsCellItem,
  GridColDef,
  GridRowId,
  gridRowSelectionIdsSelector,
  gridRowSelectionStateSelector,
  useGridApiContext,
  useGridSelector,
} from '@mui/x-data-grid-pro'
import { useSnackbar } from 'notistack'

import ButtonLink from '../../../../components/button-link'
import { useHost } from '../../../../providers/app-provider'
import Api from '../../../../utils/api'
import PeopleContext from '../../context'
import useStyles from '../../index.styles'

// specific types for the Footer
declare module '@mui/x-data-grid' {
  interface FooterPropsOverrides {
    onRowAdd: () => void
    onRowDelete: (ids: GridRowId[]) => void
    onConfirm: (newFormValues: FormValue[]) => void
    onCancel: () => void
    isDirty: boolean
  }
}

const isValidJson = (obj: unknown) => {
  try {
    JSON.parse(obj as string)
  } catch {
    return false
  }
  return true
}

interface FormValue {
  id: number
  key: string
  value: string
  asJson: boolean
}

const MetadataFooter = ({
  onRowAdd,
  onRowDelete,
  onConfirm,
  onCancel,
  isDirty,
}: {
  onRowAdd: () => void
  onRowDelete: (ids: GridRowId[]) => void
  onConfirm: (newFormValues: FormValue[]) => void
  onCancel: () => void
  isDirty: boolean
}) => {
  const classes = useStyles
  const apiRef = useGridApiContext()
  useGridSelector(apiRef, gridRowSelectionStateSelector)
  const selectedIds = Array.from(useGridSelector(apiRef, gridRowSelectionIdsSelector).keys())
  const rows = Array.from(apiRef.current.getRowModels().values()) as FormValue[]

  return (
    <>
      <Grid
        container={true}
        sx={{
          mt: 2,
          mb: 4,
          justifyContent: selectedIds.length > 0 ? 'space-between' : 'flex-end',
        }}
      >
        {selectedIds.length > 0 && (
          <Button onClick={() => onRowDelete(selectedIds)} size="small" startIcon={<DeleteOutlined />} color="error">
            Remove selected
          </Button>
        )}
        <Button onClick={onRowAdd} size="small" startIcon={<AddOutlined />} variant="outlined" data-cy="addMetadataRow">
          New row
        </Button>
      </Grid>

      <Box sx={classes.formButtonContainer}>
        <Button onClick={() => onConfirm(rows)} variant="contained" disabled={!isDirty} data-cy="confirmBtn">
          Confirm
        </Button>
        <Button onClick={onCancel} data-cy="cancelBtn">
          Cancel
        </Button>
      </Box>
    </>
  )
}

const MetadataContent = ({ isEditing, setIsEditing }: { isEditing: boolean; setIsEditing: React.Dispatch<React.SetStateAction<boolean>> }) => {
  const { user, setUser, loading } = useContext(PeopleContext)
  const host = useHost()
  const { enqueueSnackbar } = useSnackbar()
  const classes = useStyles

  const [formRows, setFormRows] = useState<FormValue[] | undefined>()
  const [isDirty, setIsDirty] = useState<boolean>(false)

  useEffect(() => {
    if (user && !formRows) {
      const rows = Object.entries(user.metadata || {}).map(([key, value], index) => ({
        id: index,
        key,
        value: typeof value === 'object' ? JSON.stringify(value) : (value as string),
        asJson: typeof value === 'object',
      }))
      setFormRows(rows)
    }
  }, [user, formRows])

  const onRowAdd = useCallback(() => {
    const newId = formRows?.length ? Math.max(...formRows.map((row) => row.id)) + 1 : 1
    const newAddedRow = {
      id: newId,
      key: `key${newId}`,
      value: '',
      asJson: false,
    }
    setFormRows(formRows?.concat(newAddedRow))
    setIsDirty(true)
  }, [formRows])

  const onRowEdit = useCallback(
    (newRow: FormValue, oldRow: FormValue) => {
      // make sure the key isn't a duplicate key
      if (newRow.key !== oldRow.key) {
        const rowsWithKey = formRows?.filter((row) => row.key === newRow.key)
        // return old row to prevent update
        if (rowsWithKey && rowsWithKey.length > 0) return oldRow
      }

      const newFormRows = formRows?.filter((row) => row.id !== newRow.id).concat({ ...newRow })
      newFormRows?.sort((a, b) => (a.id > b.id ? 1 : -1))

      setIsDirty(true)
      setFormRows(newFormRows)
      // allow update to happen
      return newRow
    },
    [formRows]
  )

  const onRowDelete = useCallback(
    (ids: GridRowId[]) => {
      setIsDirty(true)
      setFormRows(formRows?.filter((row) => !ids.includes(row.id)))
    },
    [formRows]
  )

  const onConfirm = useCallback(
    async (newFormValues: FormValue[]) => {
      if (user) {
        const res = await Api.put(`${host}/api/users/${user.id}`, {
          ...user,
          metadata: Object.fromEntries(newFormValues?.map((row) => [row.key, row.asJson && isValidJson(row.value) ? JSON.parse(row.value) : row.value]) ?? []),
        })

        if (res.ok) {
          setUser(res.body)
          setIsEditing(false)
          setIsDirty(false)
          enqueueSnackbar('Metadata updated successfully.')
        } else {
          enqueueSnackbar('Could not update metadata.', { variant: 'error' })
        }
      }
    },
    [user, host, setUser, setIsEditing, enqueueSnackbar]
  )

  const onCancel = useCallback(() => {
    setIsEditing(false)
    setFormRows(undefined)
  }, [setIsEditing])

  const toggleJson = useCallback(
    (id: number) => {
      const newRow = formRows?.find((row) => row.id === id)
      if (newRow && formRows) {
        const newFormRows = formRows.filter((row) => row.id !== id).concat({ ...newRow, asJson: !newRow.asJson })
        newFormRows.sort((a, b) => (a.id > b.id ? 1 : -1))

        setIsDirty(true)
        setFormRows(newFormRows)
      }
    },
    [formRows]
  )

  const columns = useMemo(() => {
    const cols: GridColDef[] = [
      { field: 'key', headerName: 'Key', editable: true, flex: 1 },
      { field: 'value', headerName: 'Value', editable: true, flex: 2 },
    ]

    if (isEditing) {
      cols.push({
        field: 'actions',
        type: 'actions',
        getActions: (params) => {
          // don’t show the toggle button for the centralBookmarks
          if (params.row.key === 'centralBookmarks') {
            return []
          }

          return [
            <Tooltip
              title={`This item is currently ${
                params.row.asJson ? `${isValidJson(params.row.value) ? 'a' : 'an invalid'} json object` : 'a string'
              }. Press this button to swap to a ${params.row.asJson ? 'string' : 'json object'}`}
            >
              <GridActionsCellItem
                icon={
                  params.row.asJson ? (
                    <DataObjectOutlined sx={{ color: isValidJson(params.row.value) ? 'primary.main' : 'error.main' }} />
                  ) : (
                    <AbcOutlined sx={{ color: isValidJson(params.row.value) ? 'success.main' : undefined }} />
                  )
                }
                onClick={() => toggleJson(params.row.id)}
                label="Toggle json/text"
              />
            </Tooltip>,
          ]
        },
      })
    }

    return cols
  }, [isEditing, toggleJson])

  if (!user || loading) {
    return (
      <Grid container={true} spacing={2}>
        <Grid size={4}>
          <Skeleton />
        </Grid>
        <Grid size={8}>
          <Skeleton />
        </Grid>
        <Grid size={4}>
          <Skeleton />
        </Grid>
        <Grid size={8}>
          <Skeleton />
        </Grid>
        <Grid size={4}>
          <Skeleton />
        </Grid>
        <Grid size={8}>
          <Skeleton />
        </Grid>
      </Grid>
    )
  }

  if (!isEditing && (!formRows || Object.keys(formRows).length === 0)) {
    return (
      <Box sx={classes.subscriptionViewSection}>
        <ButtonLink
          startIcon={<AddOutlined />}
          onClick={() => {
            setIsEditing(true)
            onRowAdd()
          }}
        >
          Add Metadata
        </ButtonLink>
      </Box>
    )
  }

  return (
    <Grid container={true} sx={{ flexDirection: 'column' }}>
      <DataGridPro
        checkboxSelection={isEditing}
        disableRowSelectionOnClick={true}
        disableVirtualization={true}
        rows={formRows ?? []}
        columns={columns}
        isCellEditable={() => isEditing}
        sx={{ border: 0 }}
        hideFooter={!isEditing}
        processRowUpdate={onRowEdit}
        slots={{ footer: MetadataFooter }}
        slotProps={{ footer: { onRowAdd, onRowDelete, onConfirm, onCancel, isDirty } }}
      />
    </Grid>
  )
}

export default MetadataContent
